import { useCallback, useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useProducersStore, useRoomStore, useVideoStreamStore, useVirtualBackgroundStore } from '@src/stores';

/**
 * Virtual Background Producer Update Hook
 *
 * Automatically updates the WebRTC producer track when the virtual background changes
 * during an active call. **IMPORTANT: Only works for Meet calls, not Webinar.**
 *
 * **Purpose:**
 * - Synchronizes the active producer's video track with the current virtual background stream
 * - Handles switching between original and processed (effect-applied) streams
 * - Ensures proper cleanup when effects are removed
 *
 * **When to Use:**
 * - In RoomScreen component for **Meet calls only**
 * - Only during active calls where a producer exists
 * - Automatically applies effects to the live video stream
 *
 * **Why Not Webinar:**
 * - Webinar uses different streaming architecture (RTMP/HLS)
 * - Producer updates are handled differently in webinar mode
 * - This hook guards against webinar execution internally
 *
 * **How It Works:**
 * 1. Monitors changes to modifiedStream (processed with effect)
 * 2. When effect is applied: replaces producer track with processed stream
 * 3. When effect is removed: resets producer track to original stream
 * 4. Guards against running in webinar mode or when no producer exists
 *
 * @example
 * // In RoomScreen.tsx (Meet only)
 * const RoomScreen = () => {
 *   useVirtualBackgroundProducerUpdate(); // Automatically handles track updates
 *   return <VideoContainer />;
 * };
 */
const useVirtualBackgroundProducerUpdate = () => {
  const { appliedEffect } = useVirtualBackgroundStore(
    useShallow((state) => ({
      appliedEffect: state.appliedEffect,
    }))
  );

  const { modifiedStream, originalStream } = useVideoStreamStore(
    useShallow((state) => ({
      modifiedStream: state.modifiedStream,
      originalStream: state.originalStream,
    }))
  );

  const { producers } = useProducersStore(
    useShallow((state) => ({
      producers: state.producers,
    }))
  );

  const { meetingType } = useRoomStore(
    useShallow((state) => ({
      meetingType: state.meetingType,
    }))
  );

  // Find current video producer
  const currentProducerVideo = Array.from(producers).find(
    ([_, { kind, appData }]) => kind === 'video' && appData.mediaType === 'videoType'
  )?.[1];

  const hasActiveEffect = appliedEffect.type !== 'none';

  // Apply processed stream to producer
  const applyToProducer = useCallback(async () => {
    const isWebinar = meetingType === 'webinar';

    if (!modifiedStream || isWebinar || !currentProducerVideo) return;

    try {
      const videoTrack = modifiedStream.getVideoTracks()[0];
      if (!videoTrack) return;

      // CRITICAL: Stop the current producer track before replacing
      // This ensures any cloned tracks release the camera resource
      const currentTrack = currentProducerVideo.track;

      // Replace producer track
      await currentProducerVideo.replaceTrack({ track: videoTrack });

      // Stop the old track AFTER successful replacement
      if (currentTrack && currentTrack !== videoTrack) {
        currentTrack.stop();
      }
    } catch (error) {
      console.error('Error applying virtual background to producer:', error);
    }
  }, [modifiedStream, currentProducerVideo, meetingType]);

  // Reset to original stream
  const resetProducer = useCallback(async () => {
    if (!originalStream || !currentProducerVideo) return;

    try {
      const tracks = originalStream.getVideoTracks();
      if (tracks.length > 0) {
        const track = tracks[0];
        track.enabled = true;

        // CRITICAL: Stop the current producer track before replacing
        const currentTrack = currentProducerVideo.track;

        const clonedTrack = track.clone();
        await currentProducerVideo.replaceTrack({ track: clonedTrack });

        // Stop the old track AFTER successful replacement
        if (currentTrack && currentTrack !== clonedTrack) {
          currentTrack.stop();
        }
      }
    } catch (error) {
      console.error('[VB] Error resetting producer:', error);
    }
  }, [originalStream, currentProducerVideo]);

  // Auto-update producer when effect changes
  useEffect(() => {
    // Guard: Only execute if we're in an active call
    if (!currentProducerVideo) return;

    if (hasActiveEffect && modifiedStream) {
      applyToProducer();
    } else if (!hasActiveEffect && originalStream) {
      resetProducer();
    }
  }, [hasActiveEffect, modifiedStream, originalStream, currentProducerVideo, applyToProducer, resetProducer]);

  return null; // This is a side-effect hook, no return needed
};

export default useVirtualBackgroundProducerUpdate;
